import { useEffect, useRef } from "react";

/**
 * Os três meteoros do céu do Zênite.
 *
 * No protótipo cada um é uma animação CSS infinita que fica invisível em 92% do
 * ciclo — e, mesmo invisível, continua pedindo quadro novo sessenta vezes por
 * segundo. Sem aceleração de hardware, cada quadro recompõe a tela inteira.
 * Aqui o meteoro só anima enquanto aparece: a cada ciclo, uma passada de
 * `8%` do período, com o mesmo trajeto, o mesmo brilho e os mesmos tempos.
 * Entre uma passada e outra não há animação nenhuma rodando.
 *
 * Decoração pura: aba oculta pula a passada e movimento reduzido não agenda
 * nada (§15).
 */
const SHOTS = [
  { className: "sh1", period: 13_000, delay: 2_000 },
  { className: "sh2", period: 19_000, delay: 9_000 },
  { className: "sh3", period: 23_000, delay: 15_000 },
] as const;

/** Fração do período em que o meteoro está no céu, como no protótipo. */
const VISIBLE = 0.08;

export function Meteors() {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window.matchMedia !== "function") return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const timers: number[] = [];

    const fire = (element: HTMLElement, period: number) => {
      // Aba oculta, movimento reduzido ou modal aberto: a passada fica para o
      // próximo ciclo, sem nada rodando atrás do véu.
      if (document.hidden || motion.matches || document.documentElement.dataset.modal === "open") return;
      element.style.setProperty("--shot", `${Math.round(period * VISIBLE)}ms`);
      element.classList.remove("is-on");
      // Força o recomeço da animação quando a classe volta no mesmo quadro.
      void element.offsetWidth;
      element.classList.add("is-on");
    };

    SHOTS.forEach((shot, index) => {
      const element = root.children[index] as HTMLElement | undefined;
      if (!element) return;
      const end = () => element.classList.remove("is-on");
      element.addEventListener("animationend", end);
      timers.push(
        window.setTimeout(() => {
          fire(element, shot.period);
          timers.push(window.setInterval(() => fire(element, shot.period), shot.period));
        }, shot.delay),
      );
    });

    return () => {
      for (const timer of timers) {
        window.clearTimeout(timer);
        window.clearInterval(timer);
      }
    };
  }, []);

  return (
    <div ref={rootRef} className="z-sky" aria-hidden="true">
      {SHOTS.map((shot) => (
        <span key={shot.className} className={`z-sky-shoot ${shot.className}`} />
      ))}
    </div>
  );
}
